import React, { useEffect, useState } from 'react';
import { Button, Spinner, ErrorAlert } from '../../../components/shared';
import { cn } from '../../../utils/cn';

const ASSET_TYPE_STYLES = {
  STOCK: 'bg-emerald-50 text-emerald-700',
  BOND: 'bg-blue-50 text-blue-700',
  BALANCED: 'bg-violet-50 text-violet-700',
};

const PERFORMANCE_FIELDS = [
  { key: 'navTo3Months', label: '3 thang' },
  { key: 'navTo6Months', label: '6 thang' },
  { key: 'navTo12Months', label: '12 thang' },
  { key: 'navTo36Months', label: '36 thang' },
  { key: 'navToBeginning', label: 'Tu dau' },
];

function fmt(value) {
  if (value == null) return '--';
  return value.toLocaleString('vi-VN');
}

function fmtPercent(value) {
  if (value == null) return '--';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function fmtCurrency(value) {
  if (value == null) return '--';
  return `${value.toLocaleString('vi-VN')} VND`;
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  );
}

export default function ProductDetail({ product, onClose, onLoadDetail, onBuy, loadingDetail }) {
  const [detail, setDetail] = useState(null);
  const [buying, setBuying] = useState(false);
  const [buyError, setBuyError] = useState(null);

  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    onLoadDetail(product.id).then((data) => {
      if (!cancelled) setDetail(data);
    });
    return () => { cancelled = true; };
  }, [product, onLoadDetail]);

  const handleBuy = async () => {
    setBuying(true);
    setBuyError(null);
    try {
      const result = await onBuy(product.id);
      if (!result.success) {
        setBuyError(result.error);
      }
    } finally {
      setBuying(false);
    }
  };

  const data = detail || product;
  const { issuerInfo, fundAssetType, navChange, navToPrevious } = data;
  const isPositive = navToPrevious > 0;
  const isNegative = navToPrevious < 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="relative flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4">
          <div className="flex items-center gap-3 min-w-0">
            {issuerInfo?.avatarUrl ? (
              <img
                src={issuerInfo.avatarUrl}
                alt={issuerInfo.shortName}
                className="h-10 w-10 flex-shrink-0 rounded-lg object-contain"
              />
            ) : (
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-xs font-bold text-gray-500">
                {(data.shortName || data.code || '?').slice(0, 3)}
              </div>
            )}
            <div className="min-w-0">
              <h2 className="truncate text-base font-bold text-gray-900">{data.shortName || data.name}</h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{data.code}</span>
                {fundAssetType && (
                  <span className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-medium',
                    ASSET_TYPE_STYLES[fundAssetType.code] || 'bg-gray-100 text-gray-600'
                  )}>
                    {fundAssetType.name}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loadingDetail && !detail ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <div className="rounded-xl bg-gray-50 p-4">
              <p className="text-xs text-gray-500">NAV ({data.navDate || '--'})</p>
              <div className="mt-1 flex items-end gap-3">
                <span className="text-2xl font-bold text-gray-900">{fmt(data.nav)}</span>
                <span className={cn(
                  'text-sm font-semibold',
                  isPositive && 'text-emerald-600',
                  isNegative && 'text-red-600',
                  !isPositive && !isNegative && 'text-gray-500'
                )}>
                  {fmtPercent(navToPrevious)}
                </span>
              </div>
            </div>

            {navChange && Object.keys(navChange).length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900">Hieu suat</h3>
                <div className="grid grid-cols-5 gap-2">
                  {PERFORMANCE_FIELDS.map(({ key, label }) => {
                    const val = navChange[key];
                    return (
                      <div key={key} className="rounded-lg bg-gray-50 p-2 text-center">
                        <p className="text-xs text-gray-500">{label}</p>
                        <p className={cn(
                          'mt-0.5 text-sm font-semibold',
                          val > 0 && 'text-emerald-600',
                          val < 0 && 'text-red-600',
                          (val == null || val === 0) && 'text-gray-500'
                        )}>
                          {fmtPercent(val)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <h3 className="mb-1 text-sm font-semibold text-gray-900">Thong tin giao dich</h3>
              <div className="divide-y divide-gray-100">
                <InfoRow label="Mua toi thieu" value={fmtCurrency(data.minPurchaseValue)} />
                <InfoRow label="Phi quan ly" value={data.managementFee != null ? `${data.managementFee}%` : '--'} />
                <InfoRow label="Thoi gian thanh toan" value={data.salePaymentTerm != null ? `T+${data.salePaymentTerm}` : '--'} />
                <InfoRow label="Phien giao dich" value={data.tradingSessionFormat || '--'} />
              </div>
            </div>

            {data.productFeeLst?.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900">Bieu phi</h3>
                <div className="space-y-3">
                  {['BUY', 'SELL'].map((type) => {
                    const fees = data.productFeeLst.filter((f) => f.type === type);
                    if (fees.length === 0) return null;
                    return (
                      <div key={type}>
                        <p className="mb-1 text-xs font-medium text-gray-500">
                          {type === 'BUY' ? 'Phi mua' : 'Phi ban'}
                        </p>
                        <div className="rounded-lg border border-gray-100">
                          {fees.map((fee, i) => {
                            const unit = fee.feeUnitType === 'MONTH' ? ' thang' : ' VND';
                            const from = fee.beginVolume != null ? `${fee.beginOperator} ${fmt(fee.beginVolume)}${unit}` : '';
                            const to = fee.endVolume != null ? `${fee.endOperator} ${fmt(fee.endVolume)}${unit}` : '';
                            const range = [from, to].filter(Boolean).join(' & ');
                            return (
                              <div key={i} className={cn(
                                'flex items-center justify-between px-3 py-1.5 text-xs',
                                i > 0 && 'border-t border-gray-50'
                              )}>
                                <span className="text-gray-500">{range || '--'}</span>
                                <span className="font-medium text-gray-900">{fee.fee}%</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {issuerInfo && (
              <div>
                <h3 className="mb-1 text-sm font-semibold text-gray-900">Cong ty quan ly</h3>
                <p className="text-sm text-gray-600">{issuerInfo.name}</p>
              </div>
            )}
          </div>
        )}

        <div className="sticky bottom-0 space-y-3 border-t border-gray-100 bg-white px-6 py-4">
          <ErrorAlert error={buyError} />
          <Button size="lg" className="w-full" onClick={handleBuy} loading={buying}>
            Mua CCQ
          </Button>
        </div>
      </div>
    </div>
  );
}
